import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Text,
    Textarea,
    useToast,
} from '@chakra-ui/react';
import { useDisclosure, CloseButton } from "@chakra-ui/react";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import EventsService from '../../api/services/EventsService';
import { useEffect, useState, useRef } from "react";
import EventParticipantsCardList from "../../components/EventParticipantsCardList/EventParticipantsCardList";
import FileUploadInput from "../../components/FileUploadInput/FileUploadInput";

const validationSchema = Yup.object().shape({
    title: Yup.string().required('Wymagane'),
    parentTitle: Yup.string(),
    address: Yup.string().required('Wymagane'),
    date: Yup.string().required('Wymagane'),
    startTime: Yup.string().required('Wymagane'),
    endTime: Yup.string().required('Wymagane'),
    maxParticipants: Yup.number().positive().integer().required('Wymagane'),
    description: Yup.string(),
});

const EditEventForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const { id } = useParams();
    const [event, setEvent] = useState(location.state?.event || null);
    const [loading, setLoading] = useState(!event);
    const [coverImage, setCoverImage] = useState(event?.coverImage || null);
    const [coverImageName, setCoverImageName] = useState(event?.originalCoverImageName || null);
    const [uploading, setUploading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();

    const handleSubmit = async (values, actions) => {
        try {
            await EventsService.update({
                id: event?.id,
                title: values.title,
                parentTitle: values.parentEventTitle,
                address: values.address,
                date: values.date,
                startTime: values.startTime,
                endTime: values.endTime,
                maxParticipants: values.maxParticipants,
                description: values.description,
                coverImage: coverImage,
                coverImageName: coverImageName
            });
            toast({
                title: 'Dodano wydarzenie',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            navigate('/');
        } catch (error) {
            toast({
                title: 'Błąd przy dodawaniu',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            actions.setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            await EventsService.deleteEvent(event.id);
            toast({
                title: 'Zdarzenie usunięte.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            navigate('/');
        } catch (error) {
            toast({
                title: 'Błąd podczas usuwania zdarzenia',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            onClose();
        }
    };

    const fetchEvent = async () => {
        setLoading(true);
        try {
            const res = await EventsService.getEvent(id);
            const event = res.data.data;
            setEvent(event);
            setCoverImage(event.coverImage);
            setCoverImageName(event.originalCoverImageName);
        } catch (error) {
            toast({ title: 'Błąd pobierania danych', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!event) {
            fetchEvent();
        }
    }, [event, id]);

    if (loading || !event) {
        return <Text>Ładowanie danych wydarzenia...</Text>;
    }
    return (
        <Flex w="100%" maxW="7xl" flexDirection={"column"} px={4} pr={16} gap="32px" py="80px">
            <Flex w="100%" maxW="7xl" flexDirection={"row"} className="nav-wrapper" gap="16px" justifyContent={"space-between"} align={"center"}>
                <Flex gap="16px" onClick={() => navigate(`/`)} cursor={"pointer"}>
                    <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 15.5L1 8.5M1 8.5L8 1.5M1 8.5H19" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <Text className="nav">
                        {event?.title}
                    </Text>
                </Flex>
                <Flex>
                    <Button
                        onClick={() => setIsEditing(true)}
                        borderRadius="0" borderColor={'#0A4725'} borderWidth={1}
                        bg="#F3F3F3"
                        color="#0A4725" ml={4} px={8} py={5} fontWeight="500"
                        rightIcon={
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.48779 3.51229H3.48779C2.95736 3.51229 2.44865 3.72301 2.07358 4.09808C1.69851 4.47315 1.48779 4.98186 1.48779 5.51229V16.5123C1.48779 17.0427 1.69851 17.5514 2.07358 17.9265C2.44865 18.3016 2.95736 18.5123 3.48779 18.5123H14.4878C15.0182 18.5123 15.5269 18.3016 15.902 17.9265C16.2771 17.5514 16.4878 17.0427 16.4878 16.5123V11.5123M15.0738 2.09829C15.2583 1.90727 15.479 1.75491 15.723 1.65009C15.967 1.54527 16.2294 1.4901 16.495 1.48779C16.7605 1.48548 17.0239 1.53609 17.2697 1.63665C17.5155 1.73721 17.7388 1.88572 17.9266 2.0735C18.1144 2.26129 18.2629 2.48459 18.3634 2.73038C18.464 2.97618 18.5146 3.23954 18.5123 3.5051C18.51 3.77065 18.4548 4.03309 18.35 4.2771C18.2452 4.52111 18.0928 4.7418 17.9018 4.92629L9.31579 13.5123H6.48779V10.6843L15.0738 2.09829Z" stroke="#0A4725" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        }
                    >
                        Edytuj
                    </Button>
                    <Button
                        onClick={() => {
                            onOpen();
                        }}
                        borderRadius="0" borderColor={'#0A4725'} borderWidth={1}
                        bg="#F3F3F3"
                        color="#0A4725" ml={4} px={8} py={5} fontWeight="500"
                        rightIcon={
                            <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.5 5L15.633 17.142C15.5971 17.6466 15.3713 18.1188 15.0011 18.4636C14.6309 18.8083 14.1439 19 13.638 19H5.362C4.85614 19 4.36907 18.8083 3.99889 18.4636C3.6287 18.1188 3.40292 17.6466 3.367 17.142L2.5 5M7.5 9V15M11.5 9V15M12.5 5V2C12.5 1.73478 12.3946 1.48043 12.2071 1.29289C12.0196 1.10536 11.7652 1 11.5 1H7.5C7.23478 1 6.98043 1.10536 6.79289 1.29289C6.60536 1.48043 6.5 1.73478 6.5 2V5M1.5 5H17.5" stroke="#0A4725" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        }
                    >
                        Usuń
                    </Button>
                </Flex>
            </Flex>
            <Flex w="100%" maxW="7xl" className="event">
                <Flex>
                    <Text class="title">
                        O wydarzeniu
                    </Text>
                </Flex>
                <Formik
                    initialValues={{
                        title: event?.title ?? '',
                        parentEventTitle: event?.parentEventTitle ?? '',
                        address: event?.address ?? '',
                        date: event?.date ?? '',
                        startTime: event?.startTime ?? '',
                        endTime: event?.endTime ?? '',
                        maxParticipants: event?.maxParticipants ?? '',
                        description: event?.description ?? '',
                        coverImage: event?.coverImage
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form className="form">
                            <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
                                <Box flex={1}>
                                    <FormControl mb={4}>
                                        <FormLabel>Główna nazwa wydarzenia</FormLabel>
                                        <Field as={Input} name="title" bg="white" borderRadius={0} disabled={!isEditing} />
                                    </FormControl>
                                    <FormControl mb={4}>
                                        <FormLabel>Nazwa wydarzenia nadrzędnego (opcjonalnie)</FormLabel>
                                        <Field as={Input} name="parentEventTitle" bg="white" borderRadius={0} disabled={!isEditing} />
                                    </FormControl>
                                    <FileUploadInput isEditing={isEditing} coverImage={coverImage} coverImageName={coverImageName} onFileSelect={async (file) => {
                                        setUploading(true);
                                        try {
                                            const res = await EventsService.uploadCoverImage(file);
                                            if (res.data) {
                                                setCoverImageName(file?.name);
                                                setCoverImage(res.data);
                                                toast({ title: "Zdjęcie przesłane", status: "success" });
                                            }
                                        } catch {
                                            toast({ title: "Błąd przesyłania zdjęcia", status: "error" });
                                        } finally {
                                            setUploading(false);
                                        }
                                    }} />
                                    <FormControl mb={4}>
                                        <FormLabel>Adres wydarzenia</FormLabel>
                                        <Field as={Input} name="address" bg="white" borderRadius={0} disabled={!isEditing} />
                                    </FormControl>
                                    <Flex gap={4}>
                                        <FormControl mb={4}>
                                            <FormLabel>Data wydarzenia</FormLabel>
                                            <Field as={Input} name="date" type="date" bg="white" borderRadius={0} disabled={!isEditing} />
                                        </FormControl>
                                        <FormControl mb={4}>
                                            <FormLabel>Maksymalna ilość osób</FormLabel>
                                            <Field as={Input} name="maxParticipants" type="number" bg="white" borderRadius={0} disabled={!isEditing} />
                                        </FormControl>
                                    </Flex>
                                    <Flex gap={4}>
                                        <FormControl mb={4}>
                                            <FormLabel>Godzina rozpoczęcia</FormLabel>
                                            <Field as={Input} name="startTime" type="time" bg="white" borderRadius={0} disabled={!isEditing} />
                                        </FormControl>
                                        <FormControl mb={4}>
                                            <FormLabel>Godzina zakończenia</FormLabel>
                                            <Field as={Input} name="endTime" type="time" bg="white" borderRadius={0} disabled={!isEditing} />
                                        </FormControl>
                                    </Flex>
                                </Box>
                                <Box flex={1}>
                                    <FormControl mb={4}>
                                        <FormLabel>Opis wydarzenia</FormLabel>
                                        <Field as={Textarea} name="description" rows={17} bg="white" borderRadius={0} whiteSpace={"pre-wrap"} disabled={!isEditing} />
                                    </FormControl>
                                </Box>
                            </Flex>
                            <Flex justify="right" gap={4} mt={6}>
                                <Button variant="outline" borderRadius={0} onClick={() => navigate('/')} py="25px" hidden={!isEditing}>Anuluj</Button>
                                <Button type="submit" isLoading={isSubmitting} bg="brand.500" color="white" borderRadius={0} _hover={{ bg: 'brand.600' }} px="48px" py="25px" hidden={!isEditing}>
                                    Zapisz zmiany
                                </Button>
                            </Flex>
                        </Form>
                    )}
                </Formik>
            </Flex>
            <Flex w="100%" maxW="7xl" className="participants">
                <Flex>
                    <Text class="title">
                        Zapisani uczestnicy
                    </Text>
                </Flex>
                <Flex>
                    {event && <EventParticipantsCardList eventId={event.id} isEditing={isEditing}/>}
                </Flex>
            </Flex>
            {/* Modal potwierdzający usunięcie */}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
                isCentered
            >
                <AlertDialogOverlay bg="blackAlpha.300">
                    <AlertDialogContent borderRadius="0" maxW="660px" mx={4} textAlign="center" px={6} py={6} position="relative">
                        <CloseButton
                            position="absolute"
                            top="16px"
                            right="16px"
                            onClick={onClose}
                            color="black"
                        />
                        <AlertDialogHeader
                            fontSize="20px"
                            fontWeight="bold"
                            color="black"
                            pb={2}
                            textAlign="left"
                        >
                            Usuń wydarzenie
                        </AlertDialogHeader>

                        <AlertDialogBody fontSize="16px" color="black" whiteSpace="pre-line" py="48px">
                            <Text fontWeight={600} fontSize="22px">
                                Czy na pewno chcesz usunąć to wydarzenie?
                            </Text>

                            <Box mt={4}>
                                Po usunięciu wydarzenie przestanie ono być dostępne na stronie głównej.
                                Stracisz również wszystkich dotychczas zapisanych na to wydarzenie uczestników.
                            </Box>
                        </AlertDialogBody>

                        <AlertDialogFooter justifyContent="center" pt={6}>
                            <Button
                                ref={cancelRef}
                                onClick={onClose}
                                borderRadius="0"
                                border="1px solid #0A4725"
                                bg="white"
                                color="#0A4725"
                                px={8}
                                py={5}
                                fontWeight="500"
                                _hover={{ bg: "#f0f0f0" }}
                            >
                                Anuluj
                            </Button>
                            <Button
                                onClick={handleDelete}
                                borderRadius="0"
                                bg="#0A4725"
                                color="white"
                                ml={4}
                                px={8}
                                py={5}
                                fontWeight="500"
                                rightIcon={
                                    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M16.5 5L15.633 17.142C15.5971 17.6466 15.3713 18.1188 15.0011 18.4636C14.6309 18.8083 14.1439 19 13.638 19H5.362C4.85614 19 4.36907 18.8083 3.99889 18.4636C3.6287 18.1188 3.40292 17.6466 3.367 17.142L2.5 5M7.5 9V15M11.5 9V15M12.5 5V2C12.5 1.73478 12.3946 1.48043 12.2071 1.29289C12.0196 1.10536 11.7652 1 11.5 1H7.5C7.23478 1 6.98043 1.10536 6.79289 1.29289C6.60536 1.48043 6.5 1.73478 6.5 2V5M1.5 5H17.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                }
                                _hover={{ bg: "#0c5a32" }}
                            >
                                Usuń
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Flex>
    );
};

export default EditEventForm;
